/**
 * P3-S2 ▶ 내 구간 시험(FS-08-02 · 로컬 · 계약 §2) — 셸 쪽 얇은 어댑터.
 * 학생 브라우저에서 내 구간만 돈다(구간 자리 = 룸과 같은 relayLayout 좌표 · 내 기계 1대 · 내 구간 물체만 · 리모컨 없음).
 * 실제 구현은 `@picnic-field/local-host` 의 startRelayStageTest(README '내 구간 시험') — 늦게 불러온다.
 * 가짜 모드(VITE_FIELD_FAKE=1)는 dev/fake/fakeStageTest(시간 흉내 · 그리기 없음), 시험은 setRoomDepsForTests({ startStageTest }).
 * 판정 = 룸 판정(같은 observeRelayStage) — 화면은 이벤트(armed → running → done | failed)를 따라가고,
 * 그릴 수 있으면(scene) 놀이와 같은 코스 장면(CourseScene)에 load · latestFrame · onFrame 을 넘긴다.
 */
import type { RelayStagePlan } from "@picnic-field/modes";
import type { StartStageTest } from "../types";

/** 로컬 시험을 지금 쓸 수 없다(번들·기계 없음 · local-host 로드 오류 RELAY) — 학생에게는 중립 안내 */
export class StageTestUnavailable extends Error {
  constructor(why: string) {
    super(`stage test unavailable: ${why}`);
    this.name = "StageTestUnavailable";
  }
}

/** 기본 — local-host startRelayStageTest(새 Worker 호스트 · stop() 이 dispose 까지) */
export const startLocalStageTest: StartStageTest = async (req) => {
  const [{ startRelayStageTest }, defMod, bundle, runtimeConfig] = await Promise.all([
    import("@picnic-field/local-host"),
    import("@picnic-field/modes/defs/relay-machine.v1.json"),
    req.loadBundle(),
    req.loadRuntimeConfig(),
  ]);
  type Opts = Parameters<typeof startRelayStageTest>[0];
  let test: Awaited<ReturnType<typeof startRelayStageTest>>;
  try {
    test = await startRelayStageTest({
      def: defMod.default as unknown as Opts["def"],
      plan: req.plan,
      stage: req.stage,
      bundle,
      runtimeConfig,
    });
  } catch (e) {
    // 구간 없음 · 릴레이 정의 아님 · 배치 오류 — 학생이 고칠 수 없는 것
    if ((e as { code?: unknown })?.code === "RELAY") throw new StageTestUnavailable("RELAY");
    throw e;
  }
  return {
    onEvent: (cb) =>
      test.onEvent((e) =>
        cb({ kind: e.kind, ticks: e.ticks, ...(e.reason ? { reason: e.reason } : {}) }),
      ),
    stop: () => test.stop(),
    scene: {
      load: test.load,
      bundle,
      latestFrame: () => test.latestFrame(),
      onFrame: (cb) => test.onFrame(cb),
    },
  };
};

/** 룸 meta.mode.stages → RelayStagePlan[](기계 번호 · 경계 · 타임아웃 — 이름 없음) */
export function planOfMeta(
  stages: readonly {
    machine: number | null;
    in: RelayStagePlan["in"];
    out: RelayStagePlan["out"];
    timeoutS: number | null;
  }[],
): RelayStagePlan[] {
  return stages.map((s, i) => ({
    machine: s.machine ?? -1 - i,
    in: s.in,
    out: s.out,
    ...(s.timeoutS !== null ? { timeoutS: s.timeoutS } : {}),
  }));
}
